/**
 * Convert seconds to pixels based on zoom level
 */
export function secondsToPx(s, zoom) {
  return s * zoom;
}

/**
 * Convert pixels to seconds based on zoom level
 */
export function pxToSeconds(px, zoom) {
  return px / zoom;
}

/**
 * Get all snap points from clips (start and end edges)
 * @param {Array} clips - Array of clip objects
 * @param {string|null} excludeClipId - Clip ID to exclude from snap points (the one being dragged/trimmed)
 * @returns {Array} Array of time positions (in seconds) that can be snapped to
 */
export function getSnapPoints(clips, excludeClipId = null) {
  const snapPoints = [0]; // Always snap to timeline start
  
  for (const clip of clips) {
    if (clip.id === excludeClipId) continue;
    
    // Add clip start edge
    snapPoints.push(clip.start);
    // Add clip end edge
    snapPoints.push(clip.start + clip.duration);
  }
  
  return snapPoints;
}

/**
 * Find the nearest snap point within threshold
 * @param {number} time - Current time position in seconds
 * @param {Array} snapPoints - Array of snap points
 * @param {number} threshold - Snap threshold in seconds
 * @returns {{ snappedTime: number, didSnap: boolean, snapDelta: number }}
 */
export function findNearestSnapPoint(time, snapPoints, threshold) {
  let nearestPoint = time;
  let nearestDistance = Infinity;
  
  for (const point of snapPoints) {
    const distance = Math.abs(time - point);
    if (distance < nearestDistance && distance <= threshold) {
      nearestDistance = distance;
      nearestPoint = point;
    }
  }
  
  return {
    snappedTime: nearestPoint,
    didSnap: nearestDistance <= threshold,
    snapDelta: nearestPoint - time,
  };
}

/**
 * Apply snapping to a clip during drag
 * Snaps both the clip's start and end edges to nearby snap points
 * @param {number} proposedStart - Proposed start time
 * @param {number} duration - Clip duration
 * @param {Array} snapPoints - Array of snap points
 * @param {number} thresholdSeconds - Snap threshold in seconds
 * @returns {{ start: number, didSnap: boolean }}
 */
export function snapClipPosition(proposedStart, duration, snapPoints, thresholdSeconds) {
  const proposedEnd = proposedStart + duration;
  
  // Check snap for start edge
  const startSnap = findNearestSnapPoint(proposedStart, snapPoints, thresholdSeconds);
  // Check snap for end edge
  const endSnap = findNearestSnapPoint(proposedEnd, snapPoints, thresholdSeconds);
  
  // Pick the closest snap
  if (startSnap.didSnap && endSnap.didSnap) {
    // Both can snap - pick the closer one
    if (Math.abs(startSnap.snapDelta) <= Math.abs(endSnap.snapDelta)) {
      return { start: startSnap.snappedTime, didSnap: true };
    } else {
      // Snap end edge, so adjust start accordingly
      return { start: endSnap.snappedTime - duration, didSnap: true };
    }
  } else if (startSnap.didSnap) {
    return { start: startSnap.snappedTime, didSnap: true };
  } else if (endSnap.didSnap) {
    return { start: endSnap.snappedTime - duration, didSnap: true };
  }
  
  return { start: proposedStart, didSnap: false };
}

/**
 * Apply snapping during left trim operation
 * @param {number} proposedStart - Proposed new start time
 * @param {Array} snapPoints - Array of snap points
 * @param {number} thresholdSeconds - Snap threshold in seconds
 * @returns {{ snappedStart: number, didSnap: boolean }}
 */
export function snapLeftTrim(proposedStart, snapPoints, thresholdSeconds) {
  const snap = findNearestSnapPoint(proposedStart, snapPoints, thresholdSeconds);
  return { snappedStart: snap.snappedTime, didSnap: snap.didSnap };
}

/**
 * Apply snapping during right trim operation
 * @param {number} proposedEnd - Proposed new end time
 * @param {Array} snapPoints - Array of snap points
 * @param {number} thresholdSeconds - Snap threshold in seconds
 * @returns {{ snappedEnd: number, didSnap: boolean }}
 */
export function snapRightTrim(proposedEnd, snapPoints, thresholdSeconds) {
  const snap = findNearestSnapPoint(proposedEnd, snapPoints, thresholdSeconds);
  return { snappedEnd: snap.snappedTime, didSnap: snap.didSnap };
}

/**
 * Default duration for image assets (in seconds)
 * Images have infinite duration but we use this as the initial clip length
 */
export const IMAGE_DEFAULT_DURATION = 2;

/**
 * Check if a URL or media type represents an image
 */
export function isImageType(url, mediaType) {
  if (mediaType === "image") return true;
  
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
  const lowerUrl = (url || '').toLowerCase();
  return imageExtensions.some(ext => lowerUrl.includes(ext));
}

export async function probeMedia(url, typeGuess) {
  if (typeGuess === "image" || isImageType(url, typeGuess)) {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;

    const dims = await new Promise((res) => {
      img.onload = () => res({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => res({ width: null, height: null });
      setTimeout(() => res({ width: img.naturalWidth || null, height: img.naturalHeight || null }), 5000);
    });

    console.log("Probed image:", url, "duration:", IMAGE_DEFAULT_DURATION, "type: image", dims.width, "x", dims.height);
    return { 
      duration: IMAGE_DEFAULT_DURATION, 
      type: "image",
      isImage: true,
      maxDuration: Infinity,
      width: dims.width,
      height: dims.height,
    };
  }

  const el = document.createElement(typeGuess === "audio" ? "audio" : "video");
  el.preload = "metadata";
  el.crossOrigin = "anonymous";
  el.src = url;
  
  try {
    await new Promise((res, rej) => {
      let resolved = false;
      
      const checkDuration = () => {
        if (resolved) return;
        if (Number.isFinite(el.duration) && el.duration > 0) {
          resolved = true;
          res();
        }
      };
      
      el.onloadedmetadata = checkDuration;
      el.ondurationchange = checkDuration;
      el.onloadeddata = checkDuration;
      el.oncanplay = checkDuration;
      el.onerror = () => {
        if (!resolved) {
          resolved = true;
          rej(new Error("Failed to load metadata (CORS?)"));
        }
      };
      
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          res();
        }
      }, 5000);
    });
    
    const duration = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : 5;
    const type = typeGuess || (el instanceof HTMLVideoElement ? "video" : "audio");
    const width = el.videoWidth || null;
    const height = el.videoHeight || null;
    
    console.log("Probed media:", url, "duration:", duration, "type:", type, width, "x", height);
    
    return { duration, type, width, height };
  } finally {
    el.onloadedmetadata = null;
    el.ondurationchange = null;
    el.onloadeddata = null;
    el.oncanplay = null;
    el.onerror = null;
    el.src = "";
    el.load();
  }
}